<script setup>
import { ref } from 'vue';
import { router, Link, useForm } from '@inertiajs/vue3'
import JetButton from '@/Jetstream/Button.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import { useStore } from 'vuex';
import { useI18n } from "vue-i18n";

const props = defineProps({
    user: Object,
    languages: Array
});

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const urlNamePrefix = organisation ? "gi." : "";
const store = useStore();

const form = useForm({
    _method: 'PUT',
    first_name: props.user.first_name,
    last_name: props.user.last_name,
    email: props.user.email,
    photo: null,
    preferred_language: props.user.preferred_language,
});

const verificationLinkSent = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);

const updateProfileInformation = () => {
    if (photoInput.value) {
        form.photo = photoInput.value.files[0];
    }

    form.post(route(urlNamePrefix+'user-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => {
            clearPhotoFileInput();
            updateLanguage();
        },
    });
};

const sendEmailVerification = () => {
    verificationLinkSent.value = true;
};

const selectNewPhoto = () => {
    photoInput.value.click();
};

const updatePhotoPreview = () => {
    const photo = photoInput.value.files[0];

    if (! photo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo);
};

const deletePhoto = () => {
    router.delete(route(urlNamePrefix+'current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null;
            clearPhotoFileInput();
        },
    });
};

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null;
    }
};

const { locale } = useI18n({ useScope: 'global' });

const updateLanguage = () => {
    if (props.user.preferred_language) {
        if (!store.state.currentSiteLanguage || (store.state.selectedLanguage && store.state.selectedLanguage != 'picker')) {
            router.post("/api/locale", {
                'language': props.user.preferred_language,
            }, {
                preserveState: false,
                replace: true,
                preserveScroll: false,
                forceFormData: true
            });
            store.commit('switchLanguage', props.user.preferred_language);
            store.commit('maskAsSelected', 'user');
            locale.value = props.user.preferred_language;
        }
    }
}
</script>

<template>
    <JetFormSection @submitted="updateProfileInformation">
        <template #title>
            {{ $t('user.profile.title') }}
        </template>

        <template #description>
            {{ $t('user.profile.description') }}
        </template>

        <template #form>
            <!-- Profile Photo -->
            <div v-if="$page.props.jetstream.managesProfilePhotos" class="col-span-6 sm:col-span-4">
                <!-- Profile Photo File Input -->
                <input
                    ref="photoInput"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                >

                <JetLabel for="photo" :value="$t('user.profile.photo')" />

                <!-- Current Profile Photo -->
                <div v-show="! photoPreview" class="mt-2">
                    <img :src="user.profile_photo_url" :alt="user.first_name + ' ' + user.last_name" class="rounded-full h-20 w-20 object-cover">
                </div>

                <!-- New Profile Photo Preview -->
                <div v-show="photoPreview" class="mt-2">
                    <span
                        class="block rounded-full w-20 h-20 bg-cover bg-no-repeat bg-center"
                        :style="'background-image: url(\'' + photoPreview + '\');'"
                    />
                </div>

                <JetSecondaryButton class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                    {{ $t('user.profile.photo_button') }}
                </JetSecondaryButton>

                <JetSecondaryButton
                    v-if="user.profile_photo_path"
                    type="button"
                    class="mt-2"
                    @click.prevent="deletePhoto"
                >
                    {{ $t('user.profile.remove_photo_button') }}
                </JetSecondaryButton>

                <JetInputError :message="form.errors.photo" class="mt-2" />
            </div>

            <!-- First Name -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="first_name" :value="$t('user.profile.first_name')" />
                <JetInput
                    id="first_name"
                    v-model="form.first_name"
                    type="text"
                    class="mt-1 block w-full"
                    autocomplete="first_name"
                />
                <JetInputError :message="form.errors.first_name" class="mt-2" />
            </div>

            <!-- Last Name -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="last_name" :value="$t('user.profile.last_name')" />
                <JetInput
                    id="last_name"
                    v-model="form.last_name"
                    type="text"
                    class="mt-1 block w-full"
                    autocomplete="last_name"
                />
                <JetInputError :message="form.errors.last_name" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="email" :value="$t('user.profile.email')" />
                <JetInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                />
                <JetInputError :message="form.errors.email" class="mt-2" />

                <div v-if="$page.props.jetstream.hasEmailVerification && user.email_verified_at === null">
                    <p class="text-sm mt-2">
                        {{ $t('user.profile.email_unverified') }}

                        <Link
                            :href="route(urlNamePrefix+'verification.send')"
                            method="post"
                            as="button"
                            class="underline text-gray-600 hover:text-gray-900"
                            @click.prevent="sendEmailVerification"
                        >
                            {{ $t('user.profile.email_unverified_button') }}
                        </Link>
                    </p>

                    <div v-show="verificationLinkSent" class="mt-2 font-medium text-sm text-green-600">
                        {{ $t('user.profile.email_verification_sent') }}
                    </div>
                </div>
            </div>

            <!-- Preferred Language -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="preferred_language" :value="$t('user.profile.preferred_language')" />
                <select v-if="languages"
                    v-model="form.preferred_language"
                    :name="preferred_language"
                    :id="preferred_language"
                    class="mt-1 block w-full"
                    >
                        <option v-for="(language, index) in languages"
                            :name="language"
                            :value="index"
                        >{{language}}</option>
                </select>
                <JetInputError :message="form.errors.preferred_language" class="mt-2" />
            </div>
        </template>

        <template #actions>
            <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                {{ $t('user.profile.success') }}
            </JetActionMessage>

            <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                {{ $t('user.profile.submit') }}
            </JetButton>
        </template>
    </JetFormSection>
</template>
